import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  CircularProgress,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Badge,
  Tooltip,
  IconButton,
  Menu,
  Snackbar,
} from '@mui/material';
import {
  Factory,
  Person,
  Assignment,
  Timer,
  CheckCircle,
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  AccessTime,
  Speed,
  VerifiedUser,
  FiberManualRecord,
  MoreVert,
} from '@mui/icons-material';
import apiService from '../../services/api';

// Shop-floor service speaks through the gateway on 3410; direct port is 3402 or similar
const SF_BASE_URL =
  process.env.REACT_APP_SHOPFLOOR_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

// ─── Types ────────────────────────────────────────────────────────────────────
interface WorkStation {
  id: string;
  name: string;
  operator?: string;
  currentWorkOrder?: string;
  status: 'idle' | 'running' | 'setup' | 'maintenance' | 'active' | 'break' | 'offline';
  efficiency: number;
  operatorId?: string;
}

interface OperatorSession {
  id?: string | number;
  operatorId?: string;
  operatorName?: string;
  name?: string;
  stationId?: string;
  workOrderId?: string;
  currentWorkOrder?: string;
  status?: string;
  loginTime?: string;
  sessionStart?: string;
  efficiency?: number;
  output?: number;
  skillLevel?: string;
  station?: string;
  [key: string]: any;
}

interface ProductionEvent {
  id?: string | number;
  timestamp?: string;
  time?: string;
  operatorId?: string;
  operatorName?: string;
  operator?: string;
  workOrderId?: string;
  workOrder?: string;
  eventType?: string;
  event?: string;
  type?: string;
  quantity?: number;
  station?: string;
  stationId?: string;
  [key: string]: any;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function sfFetch(path: string) {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${SF_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

async function sfRequest(method: 'POST' | 'PUT', path: string, body?: object) {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${SF_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

const getStatusColor = (
  status: WorkStation['status']
): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'running':
    case 'active':
      return 'success';
    case 'setup':
    case 'break':
      return 'warning';
    case 'maintenance':
    case 'offline':
      return 'error';
    case 'idle':
    default:
      return 'default';
  }
};

const getEventTypeColor = (
  type?: string
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const t = (type ?? '').toLowerCase();
  if (t.includes('complet') || t.includes('pass') || t.includes('success')) return 'success';
  if (t.includes('fail') || t.includes('error') || t.includes('breakdown')) return 'error';
  if (t.includes('warn') || t.includes('maintenance') || t.includes('break')) return 'warning';
  return 'info';
};

// ─── Skeleton rows ────────────────────────────────────────────────────────────
const SkeletonRows: React.FC<{ rows?: number; cols?: number }> = ({ rows = 4, cols = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={r}>
        {Array.from({ length: cols }).map((__, c) => (
          <TableCell key={c}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

// ─── Tab 1: Operator Dashboard ────────────────────────────────────────────────
const OperatorDashboard: React.FC<{ workStations: WorkStation[] }> = ({ workStations }) => {
  const [sessions, setSessions] = useState<OperatorSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Try dedicated operator sessions endpoint first, then fall back to work-orders
      let data: any = null;
      try {
        data = await sfFetch('/shopfloor/operators/sessions');
      } catch {
        try {
          data = await sfFetch('/shopfloor/operators');
        } catch {
          // Fall back to deriving from workstations
          data = null;
        }
      }

      if (data) {
        const list = data.data ?? data;
        setSessions(Array.isArray(list) ? list : []);
      } else {
        // Derive sessions from workstations prop that already have operators assigned
        const derived: OperatorSession[] = workStations
          .filter((ws) => ws.operator && ws.operator !== 'Not Assigned' && ws.operator !== 'Not assigned')
          .map((ws) => ({
            id: ws.id,
            operatorId: ws.operatorId ?? ws.operator,
            operatorName: ws.operator,
            name: ws.operator,
            stationId: ws.id,
            station: ws.name,
            currentWorkOrder: ws.currentWorkOrder,
            status: ws.status,
            efficiency: ws.efficiency,
          }));
        setSessions(derived);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load operator sessions');
    } finally {
      setLoading(false);
    }
  }, [workStations]);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = sessions.filter(
    (s) => (s.status ?? '').toLowerCase() === 'working' || (s.status ?? '').toLowerCase() === 'active' || (s.status ?? '').toLowerCase() === 'running'
  ).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Operator Dashboard</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={load}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error} - Showing data derived from workstation state.
        </Alert>
      )}

      {/* Summary row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Active Operators
              </Typography>
              <Typography variant="h4" color="success.main">
                {loading ? <Skeleton width={50} /> : activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Logged In
              </Typography>
              <Typography variant="h4" color="primary.main">
                {loading ? <Skeleton width={50} /> : sessions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Avg Efficiency
              </Typography>
              <Typography variant="h4" color="info.main">
                {loading ? (
                  <Skeleton width={60} />
                ) : sessions.length > 0 ? (
                  `${(
                    sessions.reduce((s, op) => s + (op.efficiency ?? 0), 0) / sessions.length
                  ).toFixed(0)}%`
                ) : (
                  '--'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Alert severity="info">No active operator sessions found. Operators appear when logged in to workstations.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Operator</TableCell>
                <TableCell>Operator ID</TableCell>
                <TableCell>Station</TableCell>
                <TableCell>Current Work Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Login Time</TableCell>
                <TableCell>Efficiency</TableCell>
                <TableCell>Output</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => {
                const key = session.id ?? `${session.operatorId}-${session.stationId}`;
                const status = session.status ?? 'active';
                const effVal = session.efficiency ?? 0;
                const effColor =
                  effVal >= 85 ? 'success' : effVal >= 70 ? 'warning' : 'error';
                return (
                  <TableRow key={String(key)} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                          {(session.operatorName ?? session.name ?? '?')[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {session.operatorName ?? session.name ?? '--'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {session.operatorId ?? '--'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {session.station ?? session.stationId ?? '--'}
                    </TableCell>
                    <TableCell>
                      {session.currentWorkOrder ?? session.workOrderId ?? (
                        <Typography variant="body2" color="textSecondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.toUpperCase()}
                        color={
                          status === 'working' || status === 'active' || status === 'running'
                            ? 'success'
                            : status === 'break'
                            ? 'warning'
                            : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption">
                          {session.loginTime ?? session.sessionStart
                            ? new Date(
                                session.loginTime ?? session.sessionStart ?? ''
                              ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '--'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {effVal > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={effVal}
                            color={effColor}
                            sx={{ width: 60, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="body2">{effVal.toFixed(0)}%</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          --
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {session.output != null ? (
                        <Typography variant="body2">{session.output} units</Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          --
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Tab 2: Production Events ──────────────────────────────────────────────────
interface ProductionEventsTabProps {
  onNewEventCount?: (count: number) => void;
  externalRefreshTick?: number; // increments to trigger a refresh from parent
}

const ProductionEventsTab: React.FC<ProductionEventsTabProps> = ({ onNewEventCount, externalRefreshTick }) => {
  const [events, setEvents] = useState<ProductionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [newEventCount, setNewEventCount] = useState(0);
  const prevEventCountRef = useRef<number>(0);
  const [newEvent, setNewEvent] = useState({
    eventType: '',
    workOrderId: '',
    stationId: '',
    quantity: '',
    notes: '',
  });

  const eventTypes = [
    'job_start', 'job_complete', 'quality_check', 'quality_pass', 'quality_fail',
    'material_refill', 'break_start', 'break_end', 'maintenance', 'setup_complete',
  ];

  const load = useCallback(async (isAutoRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Try the production events endpoint on the gateway
      let data: any = null;
      try {
        data = await sfFetch('/shopfloor/production/events');
      } catch {
        try {
          data = await sfFetch('/shopfloor/events');
        } catch (e2: any) {
          throw e2;
        }
      }
      const list = data?.data ?? data;
      const fetchedEvents: ProductionEvent[] = Array.isArray(list) ? list : [];
      setEvents(fetchedEvents);

      // Track new events since last refresh
      if (isAutoRefresh) {
        const prevCount = prevEventCountRef.current;
        const newCount = Math.max(0, fetchedEvents.length - prevCount);
        setNewEventCount((prev) => prev + newCount);
        if (onNewEventCount) onNewEventCount(newCount);
      }
      prevEventCountRef.current = fetchedEvents.length;
    } catch (e: any) {
      setError(e.message || 'Failed to load production events');
    } finally {
      setLoading(false);
    }
  }, [onNewEventCount]);

  useEffect(() => {
    load();
  }, [load]);

  // Respond to external refresh ticks (from auto-refresh)
  useEffect(() => {
    if (externalRefreshTick && externalRefreshTick > 0) {
      load(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalRefreshTick]);

  const handleRecord = async () => {
    try {
      const payload = {
        eventType: newEvent.eventType,
        workOrderId: newEvent.workOrderId || undefined,
        stationId: newEvent.stationId || undefined,
        quantity: newEvent.quantity ? Number(newEvent.quantity) : undefined,
        notes: newEvent.notes || undefined,
        timestamp: new Date().toISOString(),
      };
      await apiService.recordProductionEvent(payload);
      setRecordOpen(false);
      setNewEvent({ eventType: '', workOrderId: '', stationId: '', quantity: '', notes: '' });
      load();
    } catch (e: any) {
      // Add optimistic event to local list
      const optimistic: ProductionEvent = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        eventType: newEvent.eventType,
        event: newEvent.eventType,
        workOrderId: newEvent.workOrderId,
        stationId: newEvent.stationId,
        quantity: newEvent.quantity ? Number(newEvent.quantity) : undefined,
      };
      setEvents((prev) => [optimistic, ...prev]);
      setRecordOpen(false);
      setNewEvent({ eventType: '', workOrderId: '', stationId: '', quantity: '', notes: '' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Production Events</Typography>
          {newEventCount > 0 && (
            <Chip
              label={`+${newEventCount} new`}
              color="primary"
              size="small"
              icon={<FiberManualRecord sx={{ fontSize: '10px !important' }} />}
              onClick={() => setNewEventCount(0)}
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<Refresh />} onClick={() => { setNewEventCount(0); load(); }}>
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setRecordOpen(true)}
          >
            Record Event
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load events from shop-floor service: {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Alert severity="info">No production events found. Events appear as operators log activity.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Station</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell align="right">Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((evt) => {
                const key = evt.id ?? Math.random();
                const rawType = evt.eventType ?? evt.event ?? evt.type ?? '';
                const typeColor = getEventTypeColor(rawType);
                const displayType = rawType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                const ts = evt.timestamp ?? evt.time;
                const station = evt.station ?? evt.stationId ?? '--';
                const operator = evt.operatorName ?? evt.operator ?? evt.operatorId ?? '--';
                const wo = evt.workOrderId ?? evt.workOrder ?? '--';
                return (
                  <TableRow key={String(key)} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {ts
                          ? ts.length > 10
                            ? new Date(ts).toLocaleString()
                            : ts
                          : '--'}
                      </Typography>
                    </TableCell>
                    <TableCell>{station}</TableCell>
                    <TableCell>{operator}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={wo !== '--' ? 'primary' : 'textSecondary'}>
                        {wo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={displayType || 'Event'}
                        color={typeColor}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {evt.quantity != null ? evt.quantity : '--'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Record Event Dialog */}
      <Dialog open={recordOpen} onClose={() => setRecordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Production Event</DialogTitle>
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth required>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={newEvent.eventType}
                label="Event Type"
                onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
              >
                {eventTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Work Order ID"
              fullWidth
              value={newEvent.workOrderId}
              onChange={(e) => setNewEvent({ ...newEvent, workOrderId: e.target.value })}
              placeholder="e.g. WO-2026-001"
            />
            <TextField
              label="Station ID"
              fullWidth
              value={newEvent.stationId}
              onChange={(e) => setNewEvent({ ...newEvent, stationId: e.target.value })}
              placeholder="e.g. WS-01"
            />
            <TextField
              label="Quantity"
              type="number"
              fullWidth
              value={newEvent.quantity}
              onChange={(e) => setNewEvent({ ...newEvent, quantity: e.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={newEvent.notes}
              onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRecord}
            disabled={!newEvent.eventType}
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── KPI Data ─────────────────────────────────────────────────────────────────
interface KpiData {
  activeOperators: number | null;
  workOrdersInProgress: number | null;
  qualityPassRate: number | null;
  oee: number | null;
}

async function fetchKpiData(): Promise<KpiData> {
  const today = new Date().toISOString().split('T')[0];
  const kpi: KpiData = {
    activeOperators: null,
    workOrdersInProgress: null,
    qualityPassRate: null,
    oee: null,
  };

  // Active operators from sessions
  try {
    let sessData: any = null;
    try { sessData = await sfFetch('/shopfloor/operators/sessions'); } catch { /* ignore */ }
    if (!sessData) try { sessData = await sfFetch('/shopfloor/operators'); } catch { /* ignore */ }
    if (sessData) {
      const sessions: any[] = sessData?.data ?? sessData ?? [];
      kpi.activeOperators = Array.isArray(sessions)
        ? sessions.filter((s: any) => {
            const st = (s.status ?? '').toLowerCase();
            return st === 'active' || st === 'working' || st === 'running';
          }).length
        : 0;
    }
  } catch { /* ignore */ }

  // Work orders in progress today
  try {
    const woData = await sfFetch(`/shopfloor/workorders?date=${today}&status=in_progress`);
    const woList: any[] = woData?.data ?? woData ?? [];
    kpi.workOrdersInProgress = Array.isArray(woList) ? woList.length : 0;
  } catch { /* ignore */ }

  // Quality pass rate today — use quality service GET /quality-checks
  try {
    let qData: any = null;
    try { qData = await sfFetch(`/quality/quality-checks?limit=100`); } catch { /* ignore */ }
    if (qData) {
      const records: any[] = qData?.data ?? qData ?? [];
      if (Array.isArray(records) && records.length > 0) {
        const passed = records.filter((r: any) => {
          const res = (r.result ?? r.status ?? r.outcome ?? '').toLowerCase();
          return res === 'pass' || res === 'passed' || res === 'ok' || res === 'accept';
        }).length;
        kpi.qualityPassRate = Math.round((passed / records.length) * 100);
      }
    }
  } catch { /* ignore */ }

  // OEE from executions summary endpoint
  try {
    const oeeData = await sfFetch('/shop-floor/executions/summary');
    if (oeeData && oeeData.oee !== undefined) {
      kpi.oee = typeof oeeData.oee === 'number' ? Math.round(oeeData.oee) : null;
    }
  } catch { /* ignore */ }

  return kpi;
}

// ─── Tab 0: Work Stations ─────────────────────────────────────────────────────
interface WorkStationsTabProps {
  workStations: WorkStation[];
  loadingWS: boolean;
  onRefresh: () => void;
}

const WorkStationsTab: React.FC<WorkStationsTabProps> = ({ workStations, loadingWS, onRefresh }) => {
  // Menu anchor per row
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; stationId: string } | null>(null);

  // Busy state for start/complete buttons to prevent double-clicks
  const [busyId, setBusyId] = useState<string | null>(null);

  // Downtime dialog
  const [downtimeOpen, setDowntimeOpen] = useState(false);
  const [downtimeStation, setDowntimeStation] = useState<WorkStation | null>(null);
  const [downtimeForm, setDowntimeForm] = useState({ reason: '', duration: '', notes: '' });

  // Quality check dialog
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityStation, setQualityStation] = useState<WorkStation | null>(null);
  const [qualityForm, setQualityForm] = useState({ result: 'pass', partNumber: '', notes: '' });

  // Snackbar feedback
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Start work order ──────────────────────────────────────────────────────
  const handleStart = async (station: WorkStation) => {
    setBusyId(station.id);
    try {
      await sfRequest('POST', `/shopfloor/workorders/${station.id}/start`);
      showSnack(`Work order started on ${station.name}`);
      onRefresh();
    } catch (e: any) {
      showSnack(`Failed to start: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  // ── Complete execution (Stop) ─────────────────────────────────────────────
  const handleComplete = async (station: WorkStation) => {
    setBusyId(station.id);
    try {
      await sfRequest('PUT', `/shopfloor/executions/${station.id}/complete`);
      showSnack(`Execution completed for ${station.name}`);
      onRefresh();
    } catch (e: any) {
      showSnack(`Failed to complete: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  // ── Downtime submission ───────────────────────────────────────────────────
  const handleDowntimeSubmit = async () => {
    if (!downtimeStation) return;
    try {
      await sfRequest('POST', '/shop-floor/production/events', {
        type: 'downtime',
        stationId: downtimeStation.id,
        workOrderId: downtimeStation.currentWorkOrder,
        reason: downtimeForm.reason,
        duration: downtimeForm.duration ? Number(downtimeForm.duration) : undefined,
        notes: downtimeForm.notes || undefined,
        timestamp: new Date().toISOString(),
      });
      showSnack('Downtime event recorded');
    } catch (e: any) {
      showSnack(`Failed to record downtime: ${e.message}`, 'error');
    } finally {
      setDowntimeOpen(false);
      setDowntimeForm({ reason: '', duration: '', notes: '' });
      setDowntimeStation(null);
    }
  };

  // ── Quality check submission ──────────────────────────────────────────────
  const handleQualitySubmit = async () => {
    if (!qualityStation) return;
    try {
      await sfRequest('POST', '/shop-floor/quality/checks', {
        workstationId: qualityStation.id,
        workOrderId: qualityStation.currentWorkOrder,
        result: qualityForm.result,
        partNumber: qualityForm.partNumber || undefined,
        notes: qualityForm.notes || undefined,
        timestamp: new Date().toISOString(),
      });
      showSnack('Quality check logged');
    } catch (e: any) {
      showSnack(`Failed to log quality check: ${e.message}`, 'error');
    } finally {
      setQualityOpen(false);
      setQualityForm({ result: 'pass', partNumber: '', notes: '' });
      setQualityStation(null);
    }
  };

  const openMenu = (el: HTMLElement, stationId: string) => setMenuAnchor({ el, stationId });
  const closeMenu = () => setMenuAnchor(null);
  const menuStation = menuAnchor
    ? workStations.find((ws) => ws.id === menuAnchor.stationId) ?? null
    : null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Work Station Status
      </Typography>

      {loadingWS ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {['Station', 'Operator', 'Work Order', 'Status', 'Efficiency', 'Actions'].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <SkeletonRows rows={4} cols={6} />
            </TableBody>
          </Table>
        </TableContainer>
      ) : workStations.length === 0 ? (
        <Alert severity="info">
          No work station data available from the shop-floor service. The service may not be running yet.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Station</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Efficiency</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workStations.map((station) => {
                const isBusy = busyId === station.id;
                return (
                  <TableRow key={station.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {station.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{station.operator || 'Not assigned'}</TableCell>
                    <TableCell>{station.currentWorkOrder || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={station.status.toUpperCase()}
                        color={getStatusColor(station.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <LinearProgress
                          variant="determinate"
                          value={station.efficiency}
                          sx={{ width: 80, mr: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2">{station.efficiency}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} alignItems="center">
                        {station.status === 'idle' && (
                          <Button
                            size="small"
                            startIcon={isBusy ? <CircularProgress size={14} /> : <PlayArrow />}
                            color="success"
                            disabled={isBusy}
                            onClick={() => handleStart(station)}
                          >
                            Start
                          </Button>
                        )}
                        {(station.status === 'running' || station.status === 'active') && (
                          <>
                            <Button size="small" startIcon={<Pause />} color="warning" disabled>
                              Pause
                            </Button>
                            <Button
                              size="small"
                              startIcon={isBusy ? <CircularProgress size={14} /> : <Stop />}
                              color="error"
                              disabled={isBusy}
                              onClick={() => handleComplete(station)}
                            >
                              Complete
                            </Button>
                          </>
                        )}
                        {/* Action menu for downtime / quality check */}
                        <Tooltip title="More actions">
                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e.currentTarget, station.id)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Per-row action menu ── */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (!menuStation) return;
            // Start Work Order from menu (same as Start button)
            closeMenu();
            handleStart(menuStation);
          }}
          disabled={menuStation?.status !== 'idle'}
        >
          <PlayArrow fontSize="small" sx={{ mr: 1 }} />
          Start Work Order
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuStation) return;
            setDowntimeStation(menuStation);
            setDowntimeOpen(true);
            closeMenu();
          }}
        >
          <Stop fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          Record Downtime
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuStation) return;
            setQualityStation(menuStation);
            setQualityOpen(true);
            closeMenu();
          }}
        >
          <CheckCircle fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
          Log Quality Check
        </MenuItem>
      </Menu>

      {/* ── Downtime Dialog ── */}
      <Dialog open={downtimeOpen} onClose={() => setDowntimeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Downtime — {downtimeStation?.name}</DialogTitle>
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Reason"
              fullWidth
              required
              value={downtimeForm.reason}
              onChange={(e) => setDowntimeForm({ ...downtimeForm, reason: e.target.value })}
              placeholder="e.g. Machine breakdown, tooling change"
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              value={downtimeForm.duration}
              onChange={(e) => setDowntimeForm({ ...downtimeForm, duration: e.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={downtimeForm.notes}
              onChange={(e) => setDowntimeForm({ ...downtimeForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDowntimeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!downtimeForm.reason}
            onClick={handleDowntimeSubmit}
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quality Check Dialog ── */}
      <Dialog open={qualityOpen} onClose={() => setQualityOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Log Quality Check — {qualityStation?.name}</DialogTitle>
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth required>
              <InputLabel>Result</InputLabel>
              <Select
                value={qualityForm.result}
                label="Result"
                onChange={(e) => setQualityForm({ ...qualityForm, result: e.target.value })}
              >
                <MenuItem value="pass">Pass</MenuItem>
                <MenuItem value="fail">Fail</MenuItem>
                <MenuItem value="conditional">Conditional</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Part Number"
              fullWidth
              value={qualityForm.partNumber}
              onChange={(e) => setQualityForm({ ...qualityForm, partNumber: e.target.value })}
              placeholder="e.g. PN-12345"
            />
            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={qualityForm.notes}
              onChange={(e) => setQualityForm({ ...qualityForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQualityOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleQualitySubmit}>
            Log Check
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar feedback ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Main ShopFloor component ─────────────────────────────────────────────────
const ShopFloor: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [workStations, setWorkStations] = useState<WorkStation[]>([]);
  const [loadingWS, setLoadingWS] = useState(true);
  const [wsError, setWsError] = useState<string | null>(null);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [eventsRefreshTick, setEventsRefreshTick] = useState(0);
  const [newEventsBadge, setNewEventsBadge] = useState(0);

  // KPI state
  const [kpi, setKpi] = useState<KpiData>({ activeOperators: null, workOrdersInProgress: null, qualityPassRate: null, oee: null });
  const [kpiLoading, setKpiLoading] = useState(true);

  const loadKpi = useCallback(async () => {
    setKpiLoading(true);
    try {
      const data = await fetchKpiData();
      setKpi(data);
    } catch { /* non-fatal */ } finally {
      setKpiLoading(false);
    }
  }, []);

  const loadWorkOrders = useCallback(async () => {
    setLoadingWS(true);
    setWsError(null);
    try {
      const data = await apiService.getShopFloorWorkOrders();
      // The endpoint returns work orders; map them to workstation-like shape
      const list: any[] = data?.data ?? data ?? [];
      if (Array.isArray(list) && list.length > 0) {
        // Work orders may embed station info; keep as-is and adapt below
        const stations: WorkStation[] = list.map((wo: any) => ({
          id: wo.id ?? wo.workOrderNumber ?? String(Math.random()),
          name: wo.workStation ?? wo.stationId ?? wo.workOrderNumber ?? 'Unknown',
          operator: wo.operator ?? wo.operatorName,
          currentWorkOrder: wo.workOrderNumber ?? wo.id,
          status: (wo.status ?? 'idle').toLowerCase() as WorkStation['status'],
          efficiency: wo.efficiency ?? 0,
          operatorId: wo.operatorId,
        }));
        setWorkStations(stations);
      } else {
        setWorkStations([]);
      }
    } catch (e: any) {
      setWsError(e.message || 'Failed to load shop floor work orders');
    } finally {
      setLoadingWS(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadWorkOrders(), loadKpi()]);
    setEventsRefreshTick((t) => t + 1);
    setLastRefreshed(new Date());
  }, [loadWorkOrders, loadKpi]);

  useEffect(() => {
    loadWorkOrders();
    loadKpi();
    setLastRefreshed(new Date());
  }, [loadWorkOrders, loadKpi]);

  // Auto-refresh interval management
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshIntervalRef.current = setInterval(() => {
        refreshAll();
      }, 30000); // 30 seconds
    } else {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    }
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [autoRefresh, refreshAll]);

  const runningStations = workStations.filter(
    (ws) => ws.status === 'running' || ws.status === 'active'
  ).length;
  const totalStations = workStations.length;
  const avgEfficiency =
    totalStations > 0
      ? workStations.reduce((s, ws) => s + ws.efficiency, 0) / totalStations
      : 0;

  // Derived KPIs: fall back to workstation data when API data is unavailable
  const activeOperatorsDisplay = kpi.activeOperators !== null
    ? kpi.activeOperators
    : workStations.filter((ws) => ws.operator && ws.operator !== 'Not assigned').length;

  const workOrdersDisplay = kpi.workOrdersInProgress !== null
    ? kpi.workOrdersInProgress
    : workStations.filter((ws) => ws.currentWorkOrder).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Shop Floor Management
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Auto-refresh toggle */}
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color={autoRefresh ? 'primary' : 'textSecondary'}>
                  Auto-refresh
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            {autoRefresh && (
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: 'success.main' }} />}
                label="30s"
                size="small"
                variant="outlined"
                color="success"
              />
            )}
          </Box>
          {/* Last refreshed */}
          {lastRefreshed && (
            <Tooltip title={`Last refreshed: ${lastRefreshed.toLocaleTimeString()}`}>
              <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'default' }}>
                <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary">
                  {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </Typography>
              </Box>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshAll}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {wsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load shop floor data from service: {wsError}
        </Alert>
      )}

      {/* ── KPI Cards Row ── */}
      <Grid container spacing={2} mb={3}>
        {/* Active Operators */}
        <Grid item xs={6} sm={3} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Operators
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {kpiLoading ? <Skeleton width={50} /> : activeOperatorsDisplay}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    operator sessions
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 36, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Work Orders In Progress */}
        <Grid item xs={6} sm={3} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    WOs In Progress
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {loadingWS ? <Skeleton width={50} /> : workOrdersDisplay}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    today
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 36, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Pass Rate */}
        <Grid item xs={6} sm={3} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Quality Pass Rate
                  </Typography>
                  <Typography
                    variant="h4"
                    color={
                      kpi.qualityPassRate === null
                        ? 'textSecondary'
                        : kpi.qualityPassRate >= 95
                        ? 'success.main'
                        : kpi.qualityPassRate >= 80
                        ? 'warning.main'
                        : 'error.main'
                    }
                  >
                    {kpiLoading ? (
                      <Skeleton width={60} />
                    ) : kpi.qualityPassRate !== null ? (
                      `${kpi.qualityPassRate}%`
                    ) : (
                      '--'
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    today's records
                  </Typography>
                </Box>
                <VerifiedUser
                  sx={{
                    fontSize: 36,
                    opacity: 0.7,
                    color:
                      kpi.qualityPassRate === null
                        ? 'text.secondary'
                        : kpi.qualityPassRate >= 95
                        ? 'success.main'
                        : kpi.qualityPassRate >= 80
                        ? 'warning.main'
                        : 'error.main',
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* OEE */}
        <Grid item xs={6} sm={3} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    OEE
                  </Typography>
                  <Typography
                    variant="h4"
                    color={
                      kpi.oee === null
                        ? 'textSecondary'
                        : kpi.oee >= 85
                        ? 'success.main'
                        : kpi.oee >= 65
                        ? 'warning.main'
                        : 'error.main'
                    }
                  >
                    {kpiLoading ? (
                      <Skeleton width={60} />
                    ) : kpi.oee !== null ? (
                      `${kpi.oee}%`
                    ) : avgEfficiency > 0 ? (
                      `${avgEfficiency.toFixed(0)}%`
                    ) : (
                      '--'
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {kpi.oee !== null ? 'completed/total ops' : 'avg station efficiency'}
                  </Typography>
                </Box>
                <Speed
                  sx={{
                    fontSize: 36,
                    opacity: 0.7,
                    color:
                      kpi.oee === null && avgEfficiency === 0
                        ? 'text.secondary'
                        : (kpi.oee ?? avgEfficiency) >= 85
                        ? 'success.main'
                        : (kpi.oee ?? avgEfficiency) >= 65
                        ? 'warning.main'
                        : 'error.main',
                  }}
                />
              </Box>
              {(kpi.oee !== null || avgEfficiency > 0) && (
                <LinearProgress
                  variant="determinate"
                  value={Math.min(kpi.oee ?? avgEfficiency, 100)}
                  color={
                    (kpi.oee ?? avgEfficiency) >= 85
                      ? 'success'
                      : (kpi.oee ?? avgEfficiency) >= 65
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ mt: 1, height: 4, borderRadius: 2 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Secondary station summary row ── */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Stations
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {loadingWS ? <Skeleton width={60} /> : `${runningStations}/${totalStations}`}
                  </Typography>
                </Box>
                <Factory sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Operators Online
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {loadingWS ? (
                      <Skeleton width={40} />
                    ) : (
                      workStations.filter((ws) => ws.operator && ws.operator !== 'Not assigned').length
                    )}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Efficiency
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {loadingWS ? <Skeleton width={60} /> : `${avgEfficiency.toFixed(0)}%`}
                  </Typography>
                </Box>
                <Timer sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Work Orders
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {loadingWS ? (
                      <Skeleton width={40} />
                    ) : (
                      workStations.filter((ws) => ws.currentWorkOrder).length
                    )}
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => {
            setSelectedTab(newValue);
            // Clear badge when user opens the events tab
            if (newValue === 2) setNewEventsBadge(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Work Stations" />
          <Tab label="Operator Dashboard" />
          <Tab
            label={
              <Badge badgeContent={newEventsBadge} color="primary" max={99}>
                <Box pr={newEventsBadge > 0 ? 1.5 : 0}>Production Events</Box>
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Work Stations ── */}
      {selectedTab === 0 && (
        <WorkStationsTab
          workStations={workStations}
          loadingWS={loadingWS}
          onRefresh={loadWorkOrders}
        />
      )}

      {/* ── Tab 1: Operator Dashboard ── */}
      {selectedTab === 1 && <OperatorDashboard workStations={workStations} />}

      {/* ── Tab 2: Production Events ── */}
      {selectedTab === 2 && (
        <ProductionEventsTab
          externalRefreshTick={eventsRefreshTick}
          onNewEventCount={(count) => {
            if (count > 0) setNewEventsBadge((prev) => prev + count);
          }}
        />
      )}
    </Box>
  );
};

export default ShopFloor;
